'use client';

import { reportFrontEndError } from 'src/app/api/household/upload/frontEndLoggingService';
import { locales } from 'src/i18n/config';
import routes from 'src/routes';

import { useMessages, useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import React, { useEffect, useRef } from 'react';
import { Breadcrumb, BreadcrumbBar } from '@trussworks/react-uswds';

import CustomLink from 'src/components/CustomLink';

// This pages will have a different root to their breadcrumbs
const publicPages = ['login'];
const hiddenPages = ['no-permission', 'waitlist-attestation', 'locale-selection'];

const formatSegment = (segment: string) => segment.replace(/[_-]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const Breadcrumbs = () => {
  const t = useTranslations('components.breadcrumbs');
  const messages = useMessages() as Record<string, Record<string, Record<string, string>>>;
  const breadcrumbMessages = messages?.components?.breadcrumbs;

  const missingSegmentsRef = useRef<string[]>([]);
  missingSegmentsRef.current = [];

  useEffect(() => {
    for (const segment of missingSegmentsRef.current) {
      void reportFrontEndError({
        errorText: `No translation found for segment "${segment}"`,
        context: 'Breadcrumbs.translateSegment',
      });
    }
  });

  const translateSegment = (segment: string) => {
    if (!breadcrumbMessages || !Object.hasOwn(breadcrumbMessages, segment)) {
      missingSegmentsRef.current.push(segment);
      return formatSegment(segment);
    }
    return t(segment);
  };
  // Breakdown navigation into links
  const path = usePathname();
  const excludedSegments = ['home', ...locales];
  let segments = path.split('/').filter((segment) => segment.length > 0 && !excludedSegments.includes(segment));
  const isPublicPage = segments.length > 0 && publicPages.includes(segments[0]);

  if (segments.length > 0 && hiddenPages.includes(segments[0])) {
    return null;
  }

  if (segments[0] === 'notice') {
    segments = ['documents', 'review_notice'];
  }

  if (segments[0] === 'communication') {
    segments = ['communication', 'review_communication'];
  }

  if (segments.includes('forms')) {
    // Adjust forms path
    segments = ['apply', segments[1]];
  }

  const links = segments.map((segment, index) => {
    // These are some special cases...
    if (segments.length > 1 && segments[0] === 'apply') {
      if (index === 1) return `/forms/${segments[1]}`;
      if (index === 2) return `/forms/${segments[1]}/questions/${segments[2]}`;
    }

    return '/' + segments.slice(0, index + 1).join('/');
  });

  const breadcrumbs = segments.map((segment, index) => {
    const href = links[index];
    const isLast = index === segments.length - 1;
    return (
      <Breadcrumb key={index}>
        {isLast ? (
          <span>{translateSegment(segment)}</span>
        ) : (
          <CustomLink className="usa-breadcrumb__link" href={href}>
            <span>{translateSegment(segment)}</span>
          </CustomLink>
        )}
      </Breadcrumb>
    );
  });

  const privateRoot = (
    <CustomLink className="usa-breadcrumb__link" href="/home">
      <span>{t('home')}</span>
    </CustomLink>
  );

  const publicRoot = (
    <CustomLink className="usa-breadcrumb__link" href={routes.external.massgov.eec}>
      <span>{t('eec')}</span>
    </CustomLink>
  );

  return (
    <>
      {breadcrumbs.length ? (
        <BreadcrumbBar>
          <Breadcrumb>{isPublicPage ? publicRoot : privateRoot}</Breadcrumb>
          <>{breadcrumbs}</>
        </BreadcrumbBar>
      ) : null}
    </>
  );
};

export default Breadcrumbs;
